import {
  Avatar,
  Group,
  Modal as MantineModal,
  ScrollArea,
  Title,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";

import { IconAlert, IconEdit, IconPlus, IconTrash, IconX } from "@/components/icons";

import type { ModalProps as MantineModalProps } from "@mantine/core";
import type { IconProps } from "@tabler/icons-react";
import type { ComponentType, FC, ReactNode } from "react";

export type ModalProps = (
  | {
      type?: "default";
      icon?: ComponentType<IconProps>;
    }
  | {
      type: "add" | "edit" | "remove" | "warning";
      icon?: never;
    }
) & {
  title?: ReactNode;
  withBackdrop?: boolean;
  large?: boolean;
  hideCloseButton?: boolean;
  titleFontSize?: string;
} & Omit<MantineModalProps, "title">;

export const Modal: FC<ModalProps> = ({
  title,
  type = "default",
  icon,
  children,
  withBackdrop = false,
  hideCloseButton = false,
  large = false,
  titleFontSize = "1.375em", // default value: 22px
  ...props
}) => {
  const Icon = icon || null;
  const isMobile = useMatches({ base: true, md: false });
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const warningColor = isDark ? "var(--mantine-color-yellow-8)" : "var(--mantine-color-yellow-9)";
  const colorText = isDark ? "gray.4" : "inherit";

  return (
    <MantineModal
      fullScreen={isMobile}
      title={
        <Group gap="md">
          {type === "add" && (
            <Avatar color="primary.9" bg={isDark ? "primary.1" : undefined} size={28}>
              <IconPlus size={24} />
            </Avatar>
          )}
          {type === "default" && Icon && (
            <Avatar color="primary.9" bg={isDark ? "primary.1" : undefined} size={28}>
              <Icon size={24} />
            </Avatar>
          )}
          {type === "edit" && (
            <Avatar color="primary.9" bg={isDark ? "primary.1" : undefined} size={28}>
              <IconEdit size={24} />
            </Avatar>
          )}
          {type === "remove" && (
            <Avatar color="danger.9" bg={isDark ? "primary.1" : undefined} size={28}>
              <IconTrash size={24} />
            </Avatar>
          )}
          {type === "warning" && (
            <Avatar color={warningColor} bg={isDark ? "transparent" : undefined} size={28}>
              <IconAlert size={24} />
            </Avatar>
          )}
          <Title fz={titleFontSize} c={colorText}>
            {title}
          </Title>
        </Group>
      }
      size={large ? "xl" : "md"}
      overlayProps={
        withBackdrop
          ? {
              color: "#F9F9FB",
              opacity: 1,
              ...props.overlayProps,
            }
          : props.overlayProps
      }
      styles={{
        close: {
          color: isDark ? "var(--mantine-color-primaryDark-3)" : "var(--mantine-color-primary-7)",
        },
      }}
      withCloseButton={!hideCloseButton}
      closeButtonProps={
        hideCloseButton
          ? undefined
          : {
              icon: (
                <IconX
                  color={
                    isDark ? "var(--mantine-color-primaryDark-3)" : "var(--mantine-color-primary-7)"
                  }
                />
              ),
            }
      }
      scrollAreaComponent={ScrollArea.Autosize}
      {...props}
    >
      {children}
    </MantineModal>
  );
};
